import { Box, Link, styled, useTheme } from '@mui/material';
import { memo, useCallback, useRef, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { BasicButton } from '../../atoms';
import { Typography } from '../../atoms/Typography/Typography';
import { Icon } from '../../atoms/Icon/Icon';
import { PortfolioPieChart } from '../../molecules/PortfolioPieChart/PortfolioPieChart';
import {
    AlertSection,
    AlertBanner,
    AlertBannerLine,
    Description
} from '../../molecules/ProbabilityOfSuccessGauge/ProbabilityOfSuccessGauge';
import { ReactComponent as CheckmarkFilledIcon } from '../../../assets/icons/checkmarkFilled.svg';
import { TableWithCustomRender } from '../../molecules/TableWithCustomRender';
import { InfoTooltip } from '../../molecules/InfoTooltip';
import { GoalPriorityChip } from '../../molecules/GoalPriorityChip/GoalPriorityChip';
import { ContributionScheduleModal } from '../../molecules/ContributionScheduleTable/ContributionScheduleModal';
import { ContributionScheduleAccount } from '../../molecules/ContributionScheduleTable/ContributionScheduleTable';
import { getGoalTypeBadge } from '../../../utils/goalTypeBadgeMapper';
import { DoughnutChartDataType } from '../../../common/types';
import {
    formatCurrency,
    getGoalPriorityEnum,
    getPriorityLabel,
    priorityOptionsFlow2,
    retrySingleRunpipe
} from '../../../utils';
import { GoalTypes } from '../../../common/types/clients-entity';
import { EwsDurationKey, REALLOC_FREQUENCY_COLUMN_COUNT } from '../../../common/constants';
import { Theme } from '@mui/material';
import dayjs from 'dayjs';
import { useAppDispatch, useAppSelector } from '../../../common/hooks';
import { ReactComponent as FundSkeletonIcon } from '../../../assets/icons/fund-card-skeleton.svg';
import { ReactComponent as NoFundIcon } from '../../../assets/icons/nofunding.svg';
import { ReactComponent as TableCells } from '../../../assets/icons/tableCells.svg';
import { ReactComponent as InformationCircleIcon } from '../../../assets/icons/information-circle.svg';
import { ReactComponent as InfoGoalsIcon } from '../../../assets/icons/info-goals-page.svg';
import { FundingNavigatorFundSkeleton } from './FundingNavigatorFundSkeleton';

export interface FundingNavigatorTableProps {
    name: string;
    currentAllocatedBalance: number;
    nextContribution: number;
    nextContributionDate: string;
}

export interface FundingNavigatorGoalCardProps {
    goalName: string;
    goalType: 'retirement' | 'wealth' | 'income';
    priority: 'Need' | 'Want' | 'Wish' | 'Dream' | 'Aspire';
    iconType: string;
    iconColor?: string;
    iconBgColor?: string;
    doughnutChartData: DoughnutChartDataType[];
    groupedPortfolio: any[];
    isOnTarget: boolean;
    target: number;
    fundingSources: FundingNavigatorTableProps[];
    isGoalRealistic?: boolean;
    amount: number;
    targetDate: string;
    planStartRetirementDate?: string;
    fundError?: boolean;
    contributionAccounts?: ContributionScheduleAccount[];
    isRunpipeLoading?: boolean;
    isRunpipeSuccess?: boolean;
    runpipe_goePayLoad?: any;
    goalId?: string;
    durationKey?: EwsDurationKey;
}

const CardContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    border: `1px solid ${theme.palette.semantic.bd.default}`,
    borderRadius: '12px',
    overflow: 'hidden',
    backgroundColor: theme.palette.common.white,
    marginBlock: '16px'
}));

const LeftSection = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    width: '24%',
    padding: '16px',
    borderRight: `1px solid ${theme.palette.semantic.bd.default}`,
    '& .funding-navigator-goal-card-skeleton': {
        maxWidth: '320px !important',
        width: '100% !important'
    }
}));

const RightSection = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: 0
}));

const GoalHeader = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '4px',
    minWidth: 0
}));

const CaptionRow = styled(Box)(() => ({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    minWidth: 0,
    maxWidth: '100%'
}));

const GoalTypeGroup = styled(Box)(() => ({
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    minWidth: 0
}));

const GoalBadgeWrapper = styled(Box)(() => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: '24px',
    height: '24px'
}));

const HeaderSeparator = styled(Box)(({ theme }) => ({
    width: '1px',
    height: '17px',
    flexShrink: 0,
    backgroundColor: theme.palette.semantic.bd.default
}));

const GoalNameText = styled(Box)(() => ({
    display: 'block',
    width: '100%',
    minWidth: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
}));

const FailedRetrySection = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    minHeight: 220,
    paddingTop: 16,
    textAlign: 'center',
    borderTop: `1px solid ${theme.palette.semantic.bd.subtle}`
}));

const FailedRetryIcon = styled(Box)(({ theme }) => ({
    width: 24,
    height: 24,
    borderRadius: '50%',
    backgroundColor: theme.palette.error.main,
    color: theme.palette.common.white,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 16,
    fontWeight: 700,
    lineHeight: 1
}));

const FailedRetryTitle = styled('p')(({ theme }) => ({
    margin: 0,
    color: theme.palette.text.primary,
    fontSize: 13,
    fontWeight: 600,
    lineHeight: '18px'
}));

const FailedRetrySubtitle = styled('p')(({ theme }) => ({
    margin: 0,
    color: theme.palette.semantic.text.muted,
    fontSize: 13,
    fontWeight: 500,
    lineHeight: '18px',
    '& strong': {
        color: theme.palette.semantic.text.brand,
        textDecoration: 'underline',
        fontWeight: 600,
        cursor: 'pointer'
    }
}));

const ChartContainer = styled('div')(({ theme }) => ({
    paddingBlock: theme.spacing(0.25),
    borderTop: `1px solid ${theme.palette.semantic.bd.subtle}`,
    borderBottom: `1px solid ${theme.palette.semantic.bd.subtle}`,
    display: 'flex',
    justifyContent: 'center',
    '& .funding-navigator-goal-card-pie-chart': {
        width: '100% !important',
        marginLeft: '-0.6rem !important',
        '&-legend': {
            marginLeft: '12px !important'
        },
        '&-legend-name p': {
            marginLeft: '0px !important'
        }
    }
}));

const getAlertColor = (isOnTarget: boolean, theme: Theme) => {
    return isOnTarget ? theme.palette.success.main : theme.palette.warning.main;
};
// eslint-disable-next-line no-unused-vars
const getGoalTypeLabel = (goalType: string, t: (key: string) => string): string => {
    switch (goalType) {
        case 'retirement':
            return t('RETIREMENT_GOAL_CARD_HEADER');
        case 'income':
            return t('INCOME_GOAL_CARD_HEADER');
        case 'wealth':
            return t('WEALTH_GOAL_CARD_HEADER');
        default:
            return t('CREATE_PDF_REPORTS_TABLE_GOAL');
    }
};

interface FundingNavigatorFailedToLoadSectionProps {
    supportEmail: string;
    onRetry: () => void;
}

const FundingNavigatorFailedToLoadSection = ({ supportEmail, onRetry }: FundingNavigatorFailedToLoadSectionProps) => {
    const { t } = useTranslation();

    return (
        <FailedRetrySection>
            <FailedRetryIcon>!</FailedRetryIcon>
            <FailedRetryTitle>{t('FAILED_TO_LOAD_DATA')}</FailedRetryTitle>
            <FailedRetrySubtitle>
                <Trans
                    i18nKey="RETRY_CONTACT_US"
                    components={{
                        strong: (
                            <Link
                                href={`mailto:${supportEmail}`}
                                underline="always"
                                sx={{
                                    fontWeight: 600,
                                    color: (theme) => theme.palette.semantic.text.brand,
                                    cursor: 'pointer'
                                }}
                            />
                        )
                    }}
                />
            </FailedRetrySubtitle>
            <BasicButton
                variant="contained"
                onClick={onRetry}
                sx={{
                    minWidth: 96,
                    height: 36,
                    borderRadius: '999px',
                    paddingInline: 5,
                    fontSize: 16,
                    fontWeight: 600,
                    lineHeight: '20px',
                    textTransform: 'none'
                }}
            >
                {t('TEXT_ERROR_RETRY')}
            </BasicButton>
        </FailedRetrySection>
    );
};

const GoalNameHeading = ({ goalName }: { goalName: string }) => {
    const textRef = useRef<HTMLDivElement>(null);
    const [isTruncated, setIsTruncated] = useState(false);

    const measureTruncation = () => {
        const el = textRef.current;
        setIsTruncated(!!el && el.scrollWidth > el.clientWidth);
    };

    const heading = (
        <GoalNameText ref={textRef} onMouseEnter={measureTruncation} onFocus={measureTruncation} tabIndex={0}>
            <Typography
                as="span"
                display="inline"
                variant="body.largeBold"
                color="semantic.text.default"
                sx={{ lineHeight: '22px' }}
                label={goalName}
            />
        </GoalNameText>
    );

    if (!isTruncated) return heading;

    return (
        <InfoTooltip message={goalName} placement="bottom-start" style={{ width: '100%', minWidth: 0 }}>
            {heading}
        </InfoTooltip>
    );
};

export const FundingNavigatorGoalCardComponent = ({
    goalName,
    goalType,
    priority,
    iconType,
    iconColor,
    iconBgColor,
    doughnutChartData,
    groupedPortfolio,
    isOnTarget,
    target,
    fundingSources,
    amount,
    targetDate,
    planStartRetirementDate,
    fundError,
    contributionAccounts,
    isRunpipeLoading,
    isRunpipeSuccess,
    runpipe_goePayLoad,
    goalId,
    durationKey
}: FundingNavigatorGoalCardProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const color = getAlertColor(isOnTarget, theme);
    const { goeConfig } = useAppSelector((state) => state.global);
    const supportEmail = useAppSelector(
        (state) => state.global?.globalConfig?.supportEmail || t('APPLICATION_SUPPORT_EMAIL')
    );
    const goalPriorityValue = goeConfig?.['goalPriorityProbabilities'];
    const goalPriorityEnum = getGoalPriorityEnum(goalPriorityValue);
    const tenant = useAppSelector((state) => state.auth?.user?.tenant || state.global?.globalConfig?.subdomain);
    const priorityOption = priorityOptionsFlow2(t, goalPriorityEnum).find((option) => option.value === priority);
    const [isScheduleOpen, setIsScheduleOpen] = useState(false);
    // runpipe status: streaming loading is driven live by the prop; local state only covers a retry.
    const [isRetrying, setIsRetrying] = useState(false);
    const isRunpipeLoadingStatus = isRetrying || (isRunpipeLoading ?? false);
    const isRunpipeSuccessStatus = isRunpipeSuccess ?? true;
    const retry_runpipe = useCallback(async () => {
        if (!goalId || !durationKey || !runpipe_goePayLoad) return;
        setIsRetrying(true);
        await retrySingleRunpipe(dispatch, durationKey, goalId, runpipe_goePayLoad);
        setIsRetrying(false);
    }, [dispatch, durationKey, goalId, runpipe_goePayLoad]);

    const priorityLabel = priorityOption
        ? `${priorityOption.index + 1}-${getPriorityLabel(priorityOption, tenant, t)}`
        : undefined;

    const hasAllocatedBalance = fundingSources.some(
        (source) => Boolean(source.currentAllocatedBalance) && String(source.currentAllocatedBalance) !== '-'
    );
    const hasScheduledContribution = fundingSources.some(
        (source) => Boolean(source.nextContribution) && String(source.nextContribution) !== '-'
    );
    const reallocFrequency = goeConfig?.scheduleFrequency
        ? REALLOC_FREQUENCY_COLUMN_COUNT[goeConfig.scheduleFrequency]
        : undefined;
    const emptyStateMessage =
        !hasAllocatedBalance && !hasScheduledContribution
            ? t('NO_ALLOCATED_BALANCE_NO_CONTRIBUTIONS', { realloc_frequency: reallocFrequency })
            : !hasAllocatedBalance
            ? t('NO_CURRENT_ALLOCATED_BALANCE')
            : !hasScheduledContribution
            ? t('NO_CONTRIBUTIONS_SCHEDULED', { realloc_frequency: reallocFrequency })
            : null;
    const isViewScheduleDisabled = !hasScheduledContribution;

    const formattedAmount = formatCurrency(amount);
    const formattedTargetDate = targetDate ? dayjs(targetDate, 'DD-MM-YYYY').format('MMM YYYY') : '';
    const formattedStartDate = planStartRetirementDate
        ? dayjs(planStartRetirementDate, 'DD-MM-YYYY').format('MMM YYYY')
        : '';

    const descriptionKey =
        goalType === GoalTypes.Wealth ? 'FUNDING_NAV_CHANCE_REACHING_BY' : 'FUNDING_NAV_CHANCE_REACHING_FROM_TO';

    const descriptionValues = {
        target,
        amount: formattedAmount,
        startDate: formattedStartDate,
        endDate: formattedTargetDate
    };

    const headerSx = { padding: '16px 8px' };
    const firstHeaderSx = { ...headerSx, padding: '16px 8px 16px 16px' };
    const cellSx = { padding: '12px 8px' };
    const firstCellSx = { ...cellSx, padding: '12px 8px 12px 16px' };

    const headers = [
        {
            label: t('FUNDING_SOURCE_HEADER'),
            key: 'name',
            align: 'left' as const,
            width: '280px',
            headerSx: firstHeaderSx,
            cellSx: firstCellSx
        },
        {
            label: t('CURRENT_ALLOCATED_BALANCE_HEADER'),
            key: 'currentAllocatedBalance',
            align: 'right' as const,
            width: '220px',
            headerSx,
            cellSx,
            headerRender: () => (
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                    {t('CURRENT_ALLOCATED_BALANCE_HEADER')}
                    <InfoTooltip message={t('CURRENT_ALLOCATED_BALANCE_INFO')} icon={InfoGoalsIcon} />
                </Box>
            )
        },
        {
            label: t('NEXT_CONTRIBUTION_HEADER'),
            key: 'nextContribution',
            align: 'right' as const,
            width: '224px',
            headerSx,
            cellSx,
            render: (value: any, row: any) => {
                const hasContribution = Boolean(value) && value !== '-';
                const hasDate = Boolean(row?.nextContributionDate) && row.nextContributionDate !== '-';
                if (!hasContribution && !hasDate) {
                    return <Box component="span">-</Box>;
                }
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '24px' }}>
                        <Box component="span" sx={{ width: '120px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                            {hasContribution ? value : '-'}
                        </Box>
                        <Box
                            sx={{
                                width: '1px',
                                height: '20px',
                                backgroundColor: theme.palette.semantic.bd.default
                            }}
                        />
                        <Box
                            component="span"
                            sx={{
                                width: '60px',
                                textAlign: 'left',
                                fontSize: '12px',
                                lineHeight: '17px',
                                color: theme.palette.semantic.text.muted,
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {hasDate ? row.nextContributionDate : '-'}
                        </Box>
                    </Box>
                );
            }
        }
    ];

    const datasource: any[] = fundingSources.map((source, index) => ({
        rowkey: `${index}`,
        name: source.name,
        currentAllocatedBalance: source.currentAllocatedBalance,
        nextContribution: source.nextContribution,
        nextContributionDate: source.nextContributionDate
    }));

    // runpipe component
    let runpipe_component = null;
    if (isRunpipeLoadingStatus) {
        runpipe_component = <FundingNavigatorFundSkeleton className="funding-navigator-goal-card-skeleton" />;
    } else if (isRunpipeSuccessStatus === false) {
        runpipe_component = <FundingNavigatorFailedToLoadSection supportEmail={supportEmail} onRetry={retry_runpipe} />;
    }
    if (fundError) {
        runpipe_component = <FundSkeletonIcon className="funding-navigator-goal-card-skeleton" />;
    }

    const GoalBadge = getGoalTypeBadge(iconType);

    return (
        <CardContainer>
            <LeftSection>
                <GoalHeader>
                    <GoalNameHeading goalName={goalName} />
                    <CaptionRow>
                        <GoalTypeGroup>
                            <GoalBadgeWrapper>
                                {GoalBadge ? (
                                    <GoalBadge width={24} height={24} style={{ flexShrink: 0 }} />
                                ) : (
                                    <Icon id={iconType} size={24} />
                                )}
                            </GoalBadgeWrapper>
                            <Typography
                                variant="labels.large"
                                label={getGoalTypeLabel(goalType, t)}
                                sx={{
                                    color: theme.palette.semantic.text.muted,
                                    whiteSpace: 'nowrap',
                                    fontSize: '14px',
                                    fontWeight: 400,
                                    lineHeight: '19px'
                                }}
                            />
                        </GoalTypeGroup>
                        {priority && priorityOption && (
                            <>
                                <HeaderSeparator />
                                <GoalPriorityChip goalPriority={priority} />
                            </>
                        )}
                    </CaptionRow>
                </GoalHeader>

                {runpipe_component ? (
                    runpipe_component
                ) : (
                    <>
                        <ChartContainer>
                            <PortfolioPieChart
                                doughnutChartData={doughnutChartData}
                                groupedPortfolio={groupedPortfolio}
                                legendPosition="right"
                                showSeparators={false}
                                size={100}
                                className="funding-navigator-goal-card-pie-chart"
                            />
                        </ChartContainer>

                        <AlertSection sx={{ fontSize: theme.spacing(1.5) }}>
                            <AlertBannerLine $color={color} />
                            <Box>
                                <AlertBanner $color={color} sx={{ fontSize: theme.spacing(1.5) }}>
                                    {isOnTarget ? (
                                        <CheckmarkFilledIcon width={16} height={16} style={{ marginRight: '8px' }} />
                                    ) : (
                                        <span style={{ marginRight: '8px', display: 'inline-flex' }}>
                                            <Icon id="warning-triangle" size={16} />
                                        </span>
                                    )}
                                    <span>{t(isOnTarget ? 'PLAN_IS_ON_TARGET' : 'PLAN_NEEDS_ATTENTION')}</span>
                                </AlertBanner>
                                <Description sx={{ marginTop: 1, fontSize: theme.spacing(1.5) }}>
                                    <Trans
                                        i18nKey={descriptionKey}
                                        values={descriptionValues}
                                        components={{ bold: <strong /> }}
                                    />
                                </Description>
                            </Box>
                        </AlertSection>
                    </>
                )}
            </LeftSection>

            <RightSection>
                {!fundError ? (
                    <>
                        <TableWithCustomRender
                            headers={headers}
                            dataSource={datasource}
                            rowHeight="51px"
                            bodyRowHeight="46px"
                            variant="ftds"
                            embedded
                        />
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: emptyStateMessage ? 'space-between' : 'flex-end',
                                gap: '8px',
                                marginTop: 'auto',
                                paddingTop: '16px',
                                paddingLeft: '16px',
                                paddingRight: '16px',
                                paddingBottom: '16px'
                            }}
                        >
                            {emptyStateMessage && (
                                <Box sx={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            paddingBlock: '3px',
                                            flexShrink: 0
                                        }}
                                    >
                                        <InformationCircleIcon width={16} height={16} />
                                    </Box>
                                    <Box
                                        component="p"
                                        sx={{
                                            margin: 0,
                                            fontFamily: '"TT Commons Pro"',
                                            fontStyle: 'italic',
                                            fontWeight: 400,
                                            fontSize: '16px',
                                            lineHeight: '22px',
                                            color: theme.palette.semantic.text.default
                                        }}
                                    >
                                        {emptyStateMessage}
                                    </Box>
                                </Box>
                            )}
                            <Box
                                component="button"
                                type="button"
                                disabled={isViewScheduleDisabled}
                                onClick={() => setIsScheduleOpen(true)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    minHeight: '24px',
                                    background: 'none',
                                    border: 'none',
                                    padding: 0,
                                    cursor: isViewScheduleDisabled ? 'default' : 'pointer',
                                    color: isViewScheduleDisabled
                                        ? theme.palette.neutral[300]
                                        : theme.palette.semantic.int.selected,
                                    '& svg path': {
                                        fill: isViewScheduleDisabled
                                            ? theme.palette.neutral[300]
                                            : theme.palette.semantic.int.selected
                                    }
                                }}
                            >
                                <TableCells width={16} height={16} />
                                <Box
                                    component="span"
                                    sx={{
                                        fontWeight: 500,
                                        fontSize: '14px',
                                        lineHeight: '19px',
                                        textDecoration: 'underline'
                                    }}
                                >
                                    {t('VIEW_SCHEDULE')}
                                </Box>
                            </Box>
                        </Box>
                    </>
                ) : (
                    <NoFundContainer>
                        <CircleDiv>
                            <NoFundIcon />
                        </CircleDiv>
                        <Typography
                            variant="labels.large"
                            label={t('FUNDING_NAV_NO_FUNDING_SOURCES')}
                            sx={{
                                color: theme.palette.neutral[950],
                                textAlign: 'center',
                                fontFamily: '"TT Commons Pro"',
                                fontSize: '16px',
                                fontStyle: 'normal',
                                fontWeight: 600,
                                lineHeight: '20px' /* 125% */
                            }}
                        />
                        <Typography
                            variant="labels.large"
                            label={t('FUNDING_NAV_NO_FUNDING_SOURCES_DESCRIPTION')}
                            sx={{
                                color: theme.palette.semantic.text.muted,
                                textAlign: 'center',
                                fontVariantNumeric: 'lining-nums tabular-nums',
                                fontFamily: '"TT Commons Pro"',
                                fontSize: '13px',
                                fontStyle: 'normal',
                                fontWeight: 500,
                                lineHeight: '18px',
                                marginTop: '8px'
                            }}
                        />
                    </NoFundContainer>
                )}
            </RightSection>
            {!isViewScheduleDisabled && (
                <ContributionScheduleModal
                    open={isScheduleOpen}
                    onClose={() => setIsScheduleOpen(false)}
                    goalName={goalName}
                    iconType={iconType}
                    iconColor={iconColor}
                    iconBgColor={iconBgColor}
                    priorityLabel={priorityLabel}
                    targetDate={targetDate}
                    accounts={contributionAccounts ?? []}
                    scheduleDescription={t('FUNDING_NAV_SCHEDULE_DESCRIPTION')}
                    horizontalScroll
                    stickyFirstColumn
                    maxHeight="100%"
                    bodyRowHeight="48px"
                />
            )}
        </CardContainer>
    );
};

const NoFundContainer = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    flex: 1,
    padding: '16px'
}));

const CircleDiv = styled(Box)(({ theme }) => ({
    width: '72px',
    height: '72px',
    borderRadius: '50%',
    backgroundColor: theme.palette.semantic.bg.brandLight1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: theme.spacing(2)
}));

export const FundingNavigatorGoalCard = memo(FundingNavigatorGoalCardComponent);

export default FundingNavigatorGoalCard;
